import {IServiceFactoryExtended} from "../../service-factory-extended.interface";
import {IJackpotBetsInfoModel} from "../models/jackpot-bets-info.model.interfeace";
import {IJackpotBetsStrategy} from "./jackpot-bets-strategy.interface";
import {makeObservable, observable, runInAction} from "mobx";
import {JackpotBetsInfoModel} from "../models/jackpot-bets-info.model";
import {CGPEventSubscription} from "../../sdk/cgp-sdk.interface";
import JackpotBetsFeedRawDataInterface from "../models/jackpot-bets-feed-raw-data.interface";
import {
    WheelOfJackpotbetDialogComponent
} from "../../../components/wheel-of-jackpotbet-dialog/wheel-of-jackpotbet-dialog.component";
import IFirebaseJackpotBetFeedsData
    from "../../../../common/interfaces/jackpot-bets/jp-bets-feed-update-firebase-data.interface";
import IFirebaseJackpotBetsWinNotificationData, {
    IFirebaseJackpotBetsErrorNotificationData,
    JackpotBetRejectionReason
} from "../../../../common/interfaces/jackpot-bets/jackpot-bets-player-notification-firebase-data.interface";

export class AuthorizedJackpotbetsStrategy implements IJackpotBetsStrategy {
    constructor(private readonly services: IServiceFactoryExtended, initialJackpotBetsInfo: IJackpotBetsInfoModel | null) {
        makeObservable(this);
        this.jackpotBetsInfo = initialJackpotBetsInfo;
        this._eventSubscription = services.sdk.events.onJackpotBetsFeedUpdate(this._onJackpotBetsFeedUpdate.bind(this));
        this._onJackpotBetsWinNotificationSubscription = services.sdk.events.onJackpotBetsWinNotification(this._onWinNotification.bind(this));
        this._onJackpotBetsErrorNotificationSubscription = services.sdk.events.onJackpotBetsErrorNotification(this._onErrorNotification.bind(this));
    }

    private _errorToasterActive: boolean = false;

    setErrorToasterActive(value: boolean): void {
        this._errorToasterActive = value;
    }

    private _eventSubscription: CGPEventSubscription = null;
    private _onJackpotBetsErrorNotificationSubscription: CGPEventSubscription = null;
    private _onJackpotBetsWinNotificationSubscription: CGPEventSubscription = null;

    @observable.ref
    jackpotBetsInfo: IJackpotBetsInfoModel | null = null;

    private _onJackpotBetsFeedUpdate(firebaseData: IFirebaseJackpotBetFeedsData): void {
        try {

            const rawData: JackpotBetsFeedRawDataInterface = {
                totalWinsToday: firebaseData.sumOfWins,
                winnersCount: firebaseData.numberOfWinners,
                jackpotsPrizes: firebaseData.jpAmounts.map(jp => {
                    return {
                        jackpotId: jp.JpId,
                        amount: jp.CurrentAmount
                    }
                })
            };

            if (this.jackpotBetsInfo) {
                this.jackpotBetsInfo.updateRawInfo(rawData);
            } else {
                runInAction(() => {
                    this.jackpotBetsInfo = new JackpotBetsInfoModel(rawData, this.services);
                });
            }

        } catch (err) {
            this.services.logger.error('Failed to parse jackpot bets feed from firebase', err);
        }
    }

    private _getErrorNotificationMessageBody(errorReasonId: JackpotBetRejectionReason): string {
        let translationKey;
        //TODO: Add translations and add cases for all error cases that the product wants to include (probably won't be all of them in the enum)
        switch (errorReasonId) {
            case JackpotBetRejectionReason.InsufficientFunds:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_5';
                break;
            case JackpotBetRejectionReason.AmountExceededRegulationLimit:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_13';
                break;
            case JackpotBetRejectionReason.MinBetThresholdNotReached:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_3';
                break;
            default:
                translationKey = 'JACKPOT_BET_ERROR_TOASTER_BODY_REASON_GENERAL';
        }
        return this.services.translation.translate(translationKey);
    }

    private _shouldErrorNotificationHaveCTA(errorReasonId: JackpotBetRejectionReason): boolean {
        const reasonsWithCTA: JackpotBetRejectionReason[] = [
            JackpotBetRejectionReason.MinBetThresholdNotReached,
            JackpotBetRejectionReason.InsufficientFunds,
            JackpotBetRejectionReason.MaxStakeReached
        ];
        return reasonsWithCTA.includes(errorReasonId);
    }

    private _getErrorNotificationCTAConfig(errorReasonId: JackpotBetRejectionReason): {
        text: string;
        onClick: (closeToast: (closeReason?: string) => void) => void;
    } | null {

        if (!this._shouldErrorNotificationHaveCTA(errorReasonId)) {
            return null;
        }

        return {
            text: this.services.translation.translate('JACKPOT_BETS_ERROR_TOASTER_CTA_TEXT'),
            onClick: closeToast => {
                this.services.jackpotBet.toggleJackpotBet().then(() => {
                    this.services.jackpotBet.sendJackpotBetEvent('error_popup_opt_out_click',
                        'from_err_toaster', `err_reason_${errorReasonId}`);
                    this.setErrorToasterActive(false);
                    closeToast();
                }).catch(error => {
                    this.services.jackpotBet.sendJackpotBetTechnicalEvent("status_toggle_failed", "src_error_popup", JSON.stringify(error));
                }) ;
            }
        }
    }

    private async _onErrorNotification(firebaseData: IFirebaseJackpotBetsErrorNotificationData): Promise<void> {
        if (!firebaseData.failedReasonId) {
            this.services.jackpotBet.sendJackpotBetTechnicalEvent('error_toaster_not_shown', 'failed_reasonId_missing');
            return;
        }

        if (this._errorToasterActive) {
            //Product wants a single error toaster to be shown at a time, without stacking of notifications.
            this.services.jackpotBet.sendJackpotBetTechnicalEvent('error_toaster_not_shown',
                'previous_toaster_still_active', `new_err_reason_${firebaseData.failedReasonId}`);
            return;
        }

        let gameRunnerService = await this.services.gameRunner;
        let isInGame = Boolean(gameRunnerService.currentRunningGame);
        if (!isInGame) {
            return;
        }

        const messageBodyText: string = this._getErrorNotificationMessageBody(firebaseData.failedReasonId);

        this.services.toaster.showStandardToast({
            contentProps: {
                title: this.services.translation.translate('JACKPOT_BETS_ERROR_TOASTER_TITLE'),
                message: messageBodyText,
                button: this._getErrorNotificationCTAConfig(firebaseData.failedReasonId)
            },
            onToastShown: () => {
                this.setErrorToasterActive(true);
                this.services.jackpotBet.sendJackpotBetEvent('error_popup_view',
                    messageBodyText, `err_reason_${firebaseData.failedReasonId}`);
            },
            onToastClosed: () => {
                this.services.jackpotBet.sendJackpotBetEvent('error_popup_dismiss_click',
                    'x-button', `err_reason_${firebaseData.failedReasonId}`);
                this.setErrorToasterActive(false);
            }
        });
    }

    private _allowWinBelowBetCelebration(winAmount: number, participationFee: number): boolean {
        const allowWinBelowBetCelebration = !!this.services.configuration.data.jackpotBets?.allowWinBelowBetCelebration;
        return !allowWinBelowBetCelebration ? winAmount > participationFee : allowWinBelowBetCelebration;
    }

    private _onWinNotification(firebaseData: IFirebaseJackpotBetsWinNotificationData): void {
        const allowWinBelowBetCelebration = this._allowWinBelowBetCelebration(firebaseData.winAmount, firebaseData.parentGameBetAmount);
        const availablePrizes = this.services.jackpotBet.jackpotsInfo.getPrizes().filter(prize => Boolean(prize));
        if (availablePrizes.length === 0) {
            return;
        }
        const currentIndex = availablePrizes.findIndex(prize => prize.jackpotId === firebaseData.jackpotId);
        if (currentIndex === -1) {
            return;
        }

        this.services.jackpotBet.sendJackpotBetEvent('win_popup_view',
            `prize_index_${currentIndex}`, `prize_amount_${firebaseData.winAmount}`);

        this.services.dialog.showModal((dialogHandler) => <WheelOfJackpotbetDialogComponent
            prizes={availablePrizes.map(prize => prize.label)}
            winAmount={firebaseData.winAmount}
            dialogHandler={dialogHandler}
            winningLights={allowWinBelowBetCelebration}
            winningConfetti={allowWinBelowBetCelebration}
            prizeIndex={currentIndex}
            onContinuePlaying={() => {
                this.services.jackpotBet.sendJackpotBetEvent('win_popup_close',
                    `prize_index_${currentIndex}`, `prize_amount_${firebaseData.winAmount}`);
            }}
        />, {
            hideBorders: true,
            hideDialogHeader: true,
            forceCentralization: true,
            disableFullscreenMobile: true,
            noDefaultContentPadding: true,
            isHighPriority: true
        });
    }

    dispose(): void {
        if (this._eventSubscription) {
            this._eventSubscription.off();
        }

        if (this._onJackpotBetsErrorNotificationSubscription) {
            this._onJackpotBetsErrorNotificationSubscription.off();
        }

        if (this._onJackpotBetsWinNotificationSubscription) {
            this._onJackpotBetsWinNotificationSubscription.off();
        }

        this._eventSubscription = null;
        this._onJackpotBetsErrorNotificationSubscription = null;
    }
}